import React, { useState } from "react";
import axios from "axios";

// API URL Configuration
const URL_CONFIG = [
  {
    id: "tbo_orchestrator",
    name: "TBO API Orchestrator",
    url: "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator",
    description: "Main TBO API endpoint (AWS)",
  },
  {
    id: "localhost_dev",
    name: "Localhost Development",
    url: "http://localhost:3001/api/tbo",
    description: "Local development server",
  },
  {
    id: "staging",
    name: "Staging Environment",
    url: "https://staging-api.routechef.com/tbo",
    description: "Staging server for testing",
  },
  {
    id: "production",
    name: "Production API",
    url: "https://api.routechef.com/tbo",
    description: "Production server",
  },
];

const Test = () => {
  const [loading, setLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState(false);
  const [responseStatusMsg, setResponseStatusMsg] = useState("no new request");
  const [rawResponse, setRawResponse] = useState("");
  const [selectedURL, setSelectedURL] = useState(URL_CONFIG[0].url);

  const handleSendReqBtn = async (e) => {
    e.preventDefault();

    // Validate required variables
    if (!trace_id || !result_idx || !userToken) {
      setResponseStatus(false);
      setResponseStatusMsg("validation error: missing required fields");
      setRawResponse(
        JSON.stringify(
          {
            error: "Missing required fields",
            details: {
              trace_id: trace_id ? "present" : "missing",
              result_idx: result_idx ? "present" : "missing",
              userToken: userToken ? "present" : "missing",
            },
          },
          null,
          2
        )
      );
      console.error("Validation Error: Missing required fields");
      return;
    }

    setLoading(true);
    setResponseStatus(false);
    setResponseStatusMsg("sending request...");
    setRawResponse("");

    try {
      const requestBody = {
        intent: "fareQuote",
        TraceId: trace_id,
        ResultIndex: result_idx,
        EndUserIp: "125.63.76.234",
        From: "webbot_new:anon",
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      };

      // Find the selected URL config for better logging
      const selectedConfig = URL_CONFIG.find((cfg) => cfg.url === selectedURL);

      const response = await axios.get(selectedURL, requestBody, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
      });

      setResponseStatus(true);
      setResponseStatusMsg("success");
      setRawResponse(JSON.stringify(response.data, null, 2));
    } catch (error) {
      console.error("API Error:", error);

      let errorMessage = "";
      let errorDetails = {};

      // Detect CORS error
      if (error.code === "ERR_NETWORK" && !error.response) {
        errorMessage = "CORS Error";
        errorDetails = {
          type: "CORS Policy Error",
          message:
            "The API endpoint blocked the request due to CORS policy. The server needs to allow requests from this origin (localhost:3000).",
          technicalDetails: {
            error: "No 'Access-Control-Allow-Origin' header present",
            origin: window.location.origin,
            endpoint: selectedURL,
            suggestion:
              "Contact the API administrator to enable CORS for your origin, or use a proxy server.",
          },
          axiosError: {
            code: error.code,
            message: error.message,
          },
        };
      }
      // Network error (no internet connection)
      else if (error.code === "ERR_NETWORK") {
        errorMessage = "Network Error";
        errorDetails = {
          type: "Network Connection Error",
          message:
            "Unable to reach the server. Check your internet connection.",
          technicalDetails: {
            code: error.code,
            originalMessage: error.message,
          },
        };
      }
      // Timeout error
      else if (error.code === "ECONNABORTED") {
        errorMessage = "Timeout Error";
        errorDetails = {
          type: "Request Timeout",
          message: "The request took too long to complete.",
          technicalDetails: {
            code: error.code,
            originalMessage: error.message,
          },
        };
      }
      // HTTP error responses (4xx, 5xx)
      else if (error.response) {
        errorMessage = `HTTP ${error.response.status} Error`;
        errorDetails = {
          type: "HTTP Error Response",
          status: error.response.status,
          statusText: error.response.statusText,
          message: getHttpErrorMessage(error.response.status),
          responseData: error.response.data,
          headers: error.response.headers,
        };
      }
      // Request setup error
      else if (error.request) {
        errorMessage = "Request Error";
        errorDetails = {
          type: "Request Setup Error",
          message: "The request was made but no response was received.",
          technicalDetails: {
            error: error.message,
            code: error.code,
          },
        };
      }
      // Unknown error
      else {
        errorMessage = "Unknown Error";
        errorDetails = {
          type: "Unexpected Error",
          message: error.message || "An unexpected error occurred",
          technicalDetails: {
            error: error.toString(),
          },
        };
      }

      setResponseStatus(false);
      setResponseStatusMsg(errorMessage.toLowerCase());
      setRawResponse(JSON.stringify(errorDetails, null, 2));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get user-friendly HTTP error messages
  const getHttpErrorMessage = (status) => {
    const errorMessages = {
      400: "Bad Request - The server couldn't understand the request.",
      401: "Unauthorized - Authentication is required or has failed.",
      403: "Forbidden - You don't have permission to access this resource.",
      404: "Not Found - The requested resource doesn't exist.",
      405: "Method Not Allowed - The HTTP method is not supported.",
      408: "Request Timeout - The server timed out waiting for the request.",
      429: "Too Many Requests - Rate limit exceeded.",
      500: "Internal Server Error - The server encountered an error.",
      502: "Bad Gateway - Invalid response from the upstream server.",
      503: "Service Unavailable - The server is temporarily unavailable.",
      504: "Gateway Timeout - The upstream server didn't respond in time.",
    };
    return errorMessages[status] || `HTTP Error ${status}`;
  };

  return (
    <div className="flex w-full h-screen bg-lime-100 text-lg text-emerald-950 overflow-auto font-mono">
      <div className="m-auto p-6 w-[70%] border border-dashed border-emerald-900 rounded-lg overflow-y-auto">
        <h1 className="text-2xl font-bold mb-6 text-emerald-900 text-center">
          API Testing Dashboard
        </h1>

        {/* URL Selection Section */}
        <div className="mb-6 p-4 bg-emerald-50 rounded-lg border-2 border-emerald-900">
          <label
            htmlFor="url-dropdown"
            className="block mb-2 font-bold text-emerald-900"
          >
            Select API Endpoint:
          </label>
          <select
            id="url-dropdown"
            value={selectedURL}
            onChange={(e) => setSelectedURL(e.target.value)}
            className="w-full p-3 bg-white border-2 border-emerald-900 rounded-lg text-emerald-950 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-700 cursor-pointer"
          >
            {URL_CONFIG.map((config) => (
              <option key={config.id} value={config.url}>
                {config.name} - {config.description}
              </option>
            ))}
          </select>
          <div className="mt-3 p-2 bg-white rounded border border-emerald-700">
            <p className="text-sm text-emerald-700">
              <span className="font-bold">Current URL:</span>
            </p>
            <p className="text-sm text-blue-600 font-bold break-all mt-1">
              {selectedURL}
            </p>
          </div>
        </div>

        {/* Request Parameters Section */}
        <div className="mb-6 p-4 bg-white rounded-lg border-2 border-dashed border-emerald-900">
          <h2 className="text-lg font-bold mb-3 text-emerald-900">
            Request Parameters:
          </h2>
          <p>
            request for low cost career:{" "}
            <span className="text-blue-600 font-bold">
              {is_lcc ? "lcc" : "non lcc"}
            </span>
          </p>
          <p>
            trace_id:{" "}
            <span className="text-blue-600 font-bold">
              {trace_id ? "true" : "false"}
            </span>
          </p>
          <p>
            result_idx:{" "}
            <span className="text-blue-600 font-bold">
              {result_idx ? "true" : "false"}
            </span>
          </p>
        </div>

        {/* Send Request Button */}
        <button
          onClick={handleSendReqBtn}
          disabled={loading}
          className={`w-full max-w-md mx-auto block rounded-lg p-3 px-6 text-lime-100 font-bold text-xl transition-all ${
            loading
              ? "bg-emerald-700 cursor-not-allowed opacity-70"
              : "bg-emerald-900 hover:bg-emerald-950 hover:shadow-lg"
          }`}
        >
          {loading ? "SENDING REQUEST..." : "SEND REQUEST"}
        </button>
        {/* Response Section */}
        <div className="border-t-2 border-dashed border-emerald-900 py-6 mt-6">
          <h2 className="text-lg font-bold mb-3 text-emerald-900">
            Response Status:{" "}
            <span
              className={`${
                responseStatus
                  ? "text-green-600"
                  : responseStatusMsg.includes("cors")
                  ? "text-orange-500"
                  : responseStatusMsg.includes("validation")
                  ? "text-yellow-600"
                  : responseStatusMsg.includes("sending")
                  ? "text-blue-500"
                  : "text-red-500"
              }`}
            >
              {responseStatusMsg}
            </span>
          </h2>
          <p className="mb-2 font-bold text-emerald-900">Full Response:</p>
          <pre
            className={`p-4 rounded-lg overflow-auto max-h-96 text-sm ${
              responseStatus
                ? "bg-emerald-950 text-lime-100"
                : responseStatusMsg.includes("cors")
                ? "bg-orange-50 text-orange-900 border-2 border-orange-500"
                : responseStatusMsg.includes("validation")
                ? "bg-yellow-50 text-yellow-900 border-2 border-yellow-500"
                : "bg-red-50 text-red-900 border-2 border-red-500"
            }`}
          >
            {rawResponse || "No response yet"}
          </pre>
        </div>
      </div>
    </div>
  );
};

const is_lcc = true;

const trace_id = "cab5cd99-aa41-498c-b5e6-fd501c1e5d79";

const result_idx =
  "OB22[TBO]cEKtOOrIaPXPAaQCR5skLPpBUmIB9ao7VMATeTK9b7ZZBBaUfvz4F2wNnbLR0ebP41Y3eDwpM6gn9tFII9xYnp2I5bCpWl8tGQTmfi/XfvAO9FOXXFxt8xo7VKEsYl/TFJQpZhlPWF4IJcR6hLiXPn8tmdws2REtCAUFrTqMjOQ22fFRlA+g5InRrE8Dh6YBnSARKy7guu/hyeXQDfmKl89odA8mT3r7eRlrlPxWu4V3LNpXBlT7N80XAnAAQeSjxmo8UQWVypNxp2z9M0FplLBUW26dn+pNBOkXV6+jweWhjB2/UPVbLZKA6pu6mXLAQ1vm4INZ8jzdLw/AG0dG1dCGVYHvUCDXYG0Hrffw2b/Ok68QzfJnVpji2bDV+PM1hMoLO1IanOOiSkvM5opurr3K82MktPy/SE1sbEtHIehM+MaiiJY2EexoIu0Je8usr9shTFJJ8n/6lIG923TFlC/JGoHtBEXjBrWSiJGsr3BTtOG+Ly4//3S2Q1atjPFAgxGHacVuXk43m42Qw53pXdLTE596aFNrZcmKdnnuYP0=";

const userToken =
  "eyJhbGciOiJSUzI1NiIsImtpZCI6IjlkMjEzMGZlZjAyNTg3ZmQ4ODYxODg2OTgyMjczNGVmNzZhMTExNjUiLCJ0eXAiOiJKV1QifQ.eyJuYW1lIjoiVGhlVW5rbm93bkdNUiIsInBpY3R1cmUiOiJodHRwczovL2xoMy5nb29nbGV1c2VyY29udGVudC5jb20vYS9BQ2c4b2NJbU9SbmRGSzZZZUdFYmlMUVNsdHBnLTU4ODJoNWRuUFlydjMyQWgxTUpLcjAzVXd2az1zOTYtYyIsImlzcyI6Imh0dHBzOi8vc2VjdXJldG9rZW4uZ29vZ2xlLmNvbS9yb3V0ZWNoZWYtYjFjNWYiLCJhdWQiOiJyb3V0ZWNoZWYtYjFjNWYiLCJhdXRoX3RpbWUiOjE3NjEyOTIzNDQsInVzZXJfaWQiOiJ6Nzlib1k4NjJwaDBmaUZUbWhIbmxSRVIzZEUyIiwic3ViIjoiejc5Ym9ZODYycGgwZmlGVG1oSG5sUkVSM2RFMiIsImlhdCI6MTc2MTM2ODQ1MCwiZXhwIjoxNzYxMzcyMDUwLCJlbWFpbCI6ImthdXNoYWx5YXRoYXJ0aEBnbWFpbC5jb20iLCJlbWFpbF92ZXJpZmllZCI6dHJ1ZSwiZmlyZWJhc2UiOnsiaWRlbnRpdGllcyI6eyJnb29nbGUuY29tIjpbIjEwNjQyMDE0NDU4OTkwOTQ1MzUzNiJdLCJlbWFpbCI6WyJrYXVzaGFseWF0aGFydGhAZ21haWwuY29tIl19LCJzaWduX2luX3Byb3ZpZGVyIjoiZ29vZ2xlLmNvbSJ9fQ.sLTsg9w9M7qrGOhYjQA6kiQ3uNe8IhIDSLbiyH2F9lel01EmeOxpbJcynMdq-3S0-6UAph5fMdMrM33iLejuELfAhByrawDUSfaES8Ly1NYvuy_DjV9ozVmXH2L1-KpFN0fZwJdy3W0sT9rnM9ifb3cWo90IfoWAherUYJhVZePTuEhRVT3J6cmWV8Et7x0js_x2WoOprbCPODqmTrSzxFFIFSR4XX6nksAgzUztzx4QEz7wWrPb_9d-omr3rJeyrffVdYUs2i7Oixfhm6XG28nmShm__RZYWgywTAmV1I642xqeYsRIRdgXEVlAI_mUrayApou2C05cDteiB-831A";

export default Test;
